import { useEffect, useId, useRef, useState } from 'react'

const defaultMetrics = {
    isScrollable: false,
    thumbOffset: 0,
    thumbWidth: 0,
}

const HorizontalScrollArea = ({
    children,
    className = '',
    contentClassName = '',
    railClassName = '',
    thumbClassName = '',
    viewportClassName = '',
}) => {
    const viewportRef = useRef(null)
    const trackRef = useRef(null)
    const contentRef = useRef(null)
    const viewportId = `horizontal-scroll-viewport-${useId().replace(/:/g, '')}`
    const [metrics, setMetrics] = useState(defaultMetrics)

    const scrollToTrackPosition = (clientX) => {
        const viewport = viewportRef.current
        const track = trackRef.current

        if (!viewport || !track || !metrics.isScrollable) {
            return
        }

        const trackRect = track.getBoundingClientRect()
        const maxThumbOffset = Math.max(trackRect.width - metrics.thumbWidth, 0)
        const maxScrollLeft = Math.max(viewport.scrollWidth - viewport.clientWidth, 0)
        const targetOffset = Math.min(
            Math.max(clientX - trackRect.left - (metrics.thumbWidth / 2), 0),
            maxThumbOffset,
        )

        viewport.scrollLeft = maxThumbOffset > 0
            ? (targetOffset / maxThumbOffset) * maxScrollLeft
            : 0
    }

    const handleTrackPointerDown = (event) => {
        if (!metrics.isScrollable) {
            return
        }

        event.preventDefault()
        scrollToTrackPosition(event.clientX)
        event.currentTarget.setPointerCapture?.(event.pointerId)
    }

    const handleTrackPointerMove = (event) => {
        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
            scrollToTrackPosition(event.clientX)
        }
    }

    const handleTrackPointerUp = (event) => {
        event.currentTarget.releasePointerCapture?.(event.pointerId)
    }

    useEffect(() => {
        const viewport = viewportRef.current
        const track = trackRef.current
        const content = contentRef.current

        if (!viewport || !track || !content) {
            return undefined
        }

        let frameId = 0

        const updateMetrics = () => {
            cancelAnimationFrame(frameId)
            frameId = requestAnimationFrame(() => {
                const trackWidth = track.clientWidth
                const viewportWidth = viewport.clientWidth
                const contentWidth = content.scrollWidth
                const scrollLeft = viewport.scrollLeft
                const maxScrollLeft = Math.max(contentWidth - viewportWidth, 0)
                const isScrollable = contentWidth - viewportWidth > 1 && trackWidth > 0

                if (!isScrollable) {
                    setMetrics(defaultMetrics)
                    return
                }

                const computedThumbWidth = Math.max((viewportWidth / contentWidth) * trackWidth, 28)
                const maxThumbOffset = Math.max(trackWidth - computedThumbWidth, 0)
                const thumbOffset = maxScrollLeft > 0
                    ? (scrollLeft / maxScrollLeft) * maxThumbOffset
                    : 0

                setMetrics({
                    isScrollable,
                    thumbOffset,
                    thumbWidth: computedThumbWidth,
                })
            })
        }

        updateMetrics()

        viewport.addEventListener('scroll', updateMetrics, { passive: true })
        window.addEventListener('resize', updateMetrics)

        let resizeObserver
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(updateMetrics)
            resizeObserver.observe(viewport)
            resizeObserver.observe(track)
            resizeObserver.observe(content)
        }

        return () => {
            cancelAnimationFrame(frameId)
            viewport.removeEventListener('scroll', updateMetrics)
            window.removeEventListener('resize', updateMetrics)
            resizeObserver?.disconnect()
        }
    }, [children])

    return (
        <div className={`group ${className}`}>
            <div id={viewportId} ref={viewportRef} className={`hide-native-scrollbar overflow-x-auto ${viewportClassName}`}>
                <div ref={contentRef} className={contentClassName}>
                    {children}
                </div>
            </div>
                <div
                    ref={trackRef}
                    aria-label="Horizontal scroll"
                    aria-controls={viewportId}
                    aria-orientation="horizontal"
                    aria-valuemax={100}
                    aria-valuemin={0}
                    aria-valuenow={viewportRef.current && viewportRef.current.scrollWidth > viewportRef.current.clientWidth
                        ? Math.round((viewportRef.current.scrollLeft / (viewportRef.current.scrollWidth - viewportRef.current.clientWidth)) * 100)
                        : 0}
                    className={`touch-none cursor-pointer rounded-full bg-zinc-800/85 transition-[opacity,margin,height] duration-200 ${
                        metrics.isScrollable ? 'mt-2.5 h-[4px] opacity-100' : 'mt-0 h-0 opacity-0'
                    } group-hover:bg-zinc-700/75 ${railClassName}`}
                    onPointerCancel={handleTrackPointerUp}
                    onPointerDown={handleTrackPointerDown}
                    onPointerMove={handleTrackPointerMove}
                    onPointerUp={handleTrackPointerUp}
                    role="scrollbar"
                >
                <div
                    className={`h-full rounded-full bg-zinc-700/80 transition-[transform,width,background-color] duration-150 group-hover:bg-zinc-500/90 ${thumbClassName}`}
                    style={{
                        transform: `translateX(${metrics.thumbOffset}px)`,
                        width: metrics.thumbWidth ? `${metrics.thumbWidth}px` : '0px',
                    }}
                />
            </div>
        </div>
    )
}

export default HorizontalScrollArea
